// pages/FAQ.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Rocket, 
  DollarSign, 
  Zap, 
  Shield, 
  ArrowRight,
  MessageCircle,
  Users,
  TrendingUp,
  Smartphone,
  Handshake,
  Mail,
  Clock,
  CheckCircle
} from 'lucide-react';

function FAQ() {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const faqCategories = [
    {
      name: 'Getting started',
      icon: Rocket,
      questions: [
        {
          id: 'getting-started-1',
          question: 'Is there a free plan or free trial?',
          answer: 'Yes! We offer a 30-day free trial with full access to all features. No credit card required, and you can cancel anytime. After the trial, you can choose the plan that best fits your church\'s needs and budget.'
        },
        {
          id: 'getting-started-2',
          question: 'How long does setup take?',
          answer: 'Most churches are up and running in under 15 minutes. Simply create your account, add your members via SMS or CSV upload, and you\'re ready to start tracking attendance and sending messages. No technical skills required.'
        },
        {
          id: 'getting-started-3',
          question: 'Do our check-in staff need smartphones?',
          answer: 'No! FaithConnect works on any phone. Check-in staff can mark attendance via SMS from any mobile phone — no smartphone, app, or data plan required. This makes it accessible for volunteers who may not have smartphones.'
        }
      ]
    },
    {
      name: 'Pricing & plans',
      icon: DollarSign,
      questions: [
        {
          id: 'pricing-1',
          question: 'What\'s the difference between Starter, Growth, Church, and Cathedral?',
          answer: 'Our plans scale with your church\'s size and needs. Starter is perfect for small fellowships (up to 50 members). Growth is ideal for churches with active follow-up (up to 300 members). Church is for established churches with multiple ministries (up to 1,000 members). Cathedral is for large, multi-branch churches with custom needs.'
        },
        {
          id: 'pricing-2',
          question: 'Is pricing in Ghana Cedis?',
          answer: 'Yes! All our pricing is in Ghana Cedis (GH₵) to make budgeting simple and transparent for Ghanaian churches. No hidden fees, no currency conversion surprises.'
        },
        {
          id: 'pricing-3',
          question: 'Can we change plans later?',
          answer: 'Absolutely. You can upgrade, downgrade, or cancel your plan at any time. Changes take effect at the start of your next billing cycle. If you need a custom plan, our team is happy to work with you.'
        }
      ]
    },
    {
      name: 'Features',
      icon: Zap,
      questions: [
        {
          id: 'features-1',
          question: 'How does bulk SMS to church members work?',
          answer: 'Our SMS feature lets you send messages to your entire congregation or specific groups (like youth, choir, or prayer teams) with just a few clicks. Messages are delivered instantly to members\' phones — no data or internet required on their end. You can schedule messages, track delivery, and even see who responded.'
        },
        {
          id: 'features-2',
          question: 'How does absence follow-up get triggered?',
          answer: 'When a member misses church, FaithConnect automatically generates an alert for your follow-up team. You can set the threshold (e.g., after 2 consecutive absences) and customize the follow-up message. Your team gets a daily list of members to reach out to, making sure no one slips through the cracks.'
        }
      ]
    },
    {
      name: 'Security & support',
      icon: Shield,
      questions: [
        {
          id: 'security-1',
          question: 'Is our member data secure?',
          answer: 'Yes! We take data security seriously. All member information is encrypted and stored securely on our servers. We comply with Ghana\'s data protection regulations and follow international best practices for data security. Your data belongs to you, and we never share it with third parties.'
        },
        {
          id: 'security-2',
          question: 'What support do you offer in Ghana?',
          answer: 'We offer comprehensive support for Ghanaian churches including: Email support (response within 24 hours), WhatsApp support for quick questions, Phone support during business hours, and a dedicated account manager for Church and Cathedral plans. Our team understands the unique needs of Ghanaian churches and is here to help.'
        }
      ]
    }
  ];

  return (
    <>
      {/* Hero Section - Navy Background */}
      <div className="page-hero">
        <div className="eyebrow light" style={{ display: 'inline-flex' }}>FAQ</div>
        <h1>Frequently asked <span className="highlight">questions.</span></h1>
        <p>Everything you need to know about FaithConnect. Can't find your answer? Contact our team.</p>
      </div>

      {/* FAQ Section - White Background */}
      <section>
        <div className="wrap">
          <div className="faq-grid-modern">
            {faqCategories.map((category, catIndex) => {
              const Icon = category.icon;
              return (
                <div key={catIndex} className="animate-fadeInUp" style={{ animationDelay: `${catIndex * 100}ms` }}>
                  {/* Category Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: catIndex === 0 ? '0' : '48px',
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid var(--gold)',
                  }}>
                    <Icon size={24} style={{ color: 'var(--gold)' }} />
                    <h3 style={{
                      fontSize: '18px',
                      color: 'var(--navy)',
                      fontWeight: 700,
                      margin: 0,
                    }}>
                      {category.name}
                    </h3>
                  </div>

                  {/* Questions */}
                  {category.questions.map((item) => (
                    <div 
                      key={item.id} 
                      className={`faq-item-modern ${openItems[item.id] ? 'open' : ''}`}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        padding: '20px 0',
                      }}
                    >
                      <div 
                        className="faq-q-modern" 
                        onClick={() => toggleItem(item.id)}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: 'var(--navy)',
                          transition: 'color 0.3s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--gold)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--navy)'}
                      >
                        <span>{item.question}</span>
                        <span 
                          className="plus-modern"
                          style={{
                            fontSize: '24px',
                            color: 'var(--gold)',
                            transition: 'transform 0.3s ease',
                            flexShrink: 0,
                            marginLeft: '20px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            background: 'rgba(212, 175, 55, 0.1)',
                            transform: openItems[item.id] ? 'rotate(45deg)' : 'rotate(0deg)',
                          }}
                        >
                          <Plus size={20} />
                        </span>
                      </div>
                      <div 
                        className="faq-a-modern"
                        style={{
                          maxHeight: openItems[item.id] ? '400px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <p style={{
                          fontSize: '14.5px',
                          color: 'var(--ink-soft)',
                          lineHeight: '1.8',
                          paddingTop: '16px',
                          maxWidth: '640px',
                        }}>
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Still have questions? - Navy Gradient (matching final-cta) */}
      <section className="final-cta">
        <h2 style={{ 
          fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
          marginBottom: '16px',
        }}>
          Still have <span className="highlight">questions?</span>
        </h2>
        <p style={{ 
          fontSize: '16px',
          color: 'rgba(255,255,255,0.6)',
          lineHeight: '1.8',
          marginBottom: '32px',
          maxWidth: '480px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          We're here to help. Reach out to our team and we'll respond within 24 hours.
        </p>
        <Link to="/contact" className="btn-primary">
          Contact us <ArrowRight size={18} />
        </Link>
      </section>
    </>
  );
}

export default FAQ;